document.documentElement.classList.replace('no-js', 'js');

document.addEventListener('DOMContentLoaded', () => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-motion-reveal]').forEach((element) => {
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      element.classList.add('is-visible');
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.18 });

    observer.observe(element);
  });

  document.querySelectorAll('form[data-page-blend-finder]').forEach((form) => {
    const input = form.querySelector('input[type="search"]');
    const submit = form.querySelector('[data-page-blend-finder-submit]');
    const status = form.querySelector('[data-page-blend-finder-status]');
    const defaultLabel = submit ? submit.textContent.trim() : 'Search blends';
    const busyLabel = form.dataset.submitLabel || 'Opening product search...';

    form.addEventListener('submit', (event) => {
      const query = input && input.value.trim() ? input.value.trim() : '';
      if (!query) {
        event.preventDefault();
        if (status) {
          status.textContent = 'Enter a page topic, blend name or ingredient before searching products.';
          status.classList.add('is-error');
        }
        if (input) {
          input.setAttribute('aria-invalid', 'true');
          input.focus();
        }
        return;
      }

      if (status) {
        status.textContent = 'Opening product search for ' + query + '.';
        status.classList.remove('is-error');
      }
      if (input) input.setAttribute('aria-invalid', 'false');
      if (submit) {
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = busyLabel;
      }
    });

    input && input.addEventListener('input', () => {
      if (!status) return;
      status.classList.remove('is-error');
      input.setAttribute('aria-invalid', 'false');
      status.textContent = input.value.trim()
        ? 'Product search will use "' + input.value.trim() + '".'
        : 'Enter a page topic, blend name or ingredient before searching products.';
    });

    window.addEventListener('pageshow', () => {
      if (status) {
        status.textContent = 'Product search opens with this page topic.';
        status.classList.remove('is-error');
      }
      if (input) input.setAttribute('aria-invalid', 'false');
      if (submit) {
        submit.setAttribute('aria-busy', 'false');
        submit.textContent = defaultLabel;
      }
    });
  });

  const resetStatus = (status) => {
    if (!status) return;
    status.textContent = '';
    status.classList.remove('is-error', 'is-success');
  };

  const setStatus = (status, message, tone) => {
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', tone === 'error');
    status.classList.toggle('is-success', tone === 'success');
  };

  const readCartError = async (response) => {
    try {
      const payload = await response.json();
      return payload.description || payload.message || payload.status || 'Cart add failed';
    } catch (error) {
      return 'Cart add failed';
    }
  };

  const refreshCartCount = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) return;

      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((counter) => {
        counter.textContent = cart.item_count;
      });
      document.querySelectorAll('[data-cart-count-label]').forEach((label) => {
        label.textContent = cart.item_count + (cart.item_count === 1 ? ' item in basket.' : ' items in basket.');
      });
      document.querySelectorAll('[data-cart-count-link]').forEach((link) => {
        link.setAttribute('aria-label', 'Review cart, ' + cart.item_count + (cart.item_count === 1 ? ' item in basket' : ' items in basket'));
      });
    } catch (error) {
      // The product was added; a cart-count refresh failure should not turn success into an error.
    }
  };

  document.querySelectorAll('form[data-product-form]').forEach((form) => {
    const optionSelector = form.querySelector('[data-product-option-selector]');
    const selectedTitle = form.querySelector('[data-selected-option-title]');
    const selectedPrice = form.querySelector('[data-selected-option-price]');
    const selectedStatus = form.querySelector('[data-selected-option-status]');
    const submit = form.querySelector('[type="submit"]');
    const submitState = form.querySelector('[data-product-submit-state]');
    const quantityInput = form.querySelector('[data-product-quantity-input]');
    const quantityStatus = form.querySelector('[data-product-quantity-status]');
    const quantitySteps = form.querySelectorAll('[data-product-quantity-step]');

    const getSelectedOptionLabel = () => {
      if (!optionSelector) return 'this blend';
      const option = optionSelector.options[optionSelector.selectedIndex];
      return option ? (option.dataset.variantTitle || option.textContent.trim()) : 'this blend';
    };

    const getSelectedAvailability = () => {
      if (!optionSelector) return !submit || !submit.disabled;
      const option = optionSelector.options[optionSelector.selectedIndex];
      return option ? option.dataset.variantAvailable === 'true' : true;
    };

    const getSelectedQuantity = () => {
      if (!quantityInput) return 1;
      const quantity = Number.parseInt(quantityInput.value, 10);
      return Number.isFinite(quantity) && quantity >= 1 ? quantity : 1;
    };

    const updateSubmitAccessibility = () => {
      if (!submit) return;
      const quantity = getSelectedQuantity();
      const packLabel = quantity === 1 ? 'pack' : 'packs';
      const optionLabel = getSelectedOptionLabel();
      const availabilityLabel = getSelectedAvailability()
        ? 'Payment starts after cart review.'
        : 'This selected option is sold out.';

      submit.setAttribute('aria-label', submit.getAttribute('aria-busy') === 'true'
        ? 'Adding ' + quantity + ' ' + packLabel + ' of ' + optionLabel + ' to cart.'
        : 'Add ' + quantity + ' ' + packLabel + ' of ' + optionLabel + ' to cart. ' + availabilityLabel);
    };

    const setQuantityStatus = (message) => {
      if (!quantityStatus) return;
      quantityStatus.textContent = message;
    };

    const syncQuantitySteps = (quantity) => {
      quantitySteps.forEach((button) => {
        if (button.dataset.productQuantityStep === '-1') {
          const isDisabled = quantity <= 1;
          button.disabled = isDisabled;
          button.setAttribute('aria-disabled', String(isDisabled));
          button.title = isDisabled ? 'Minimum quantity is one pack' : 'Decrease quantity by one pack';
        } else {
          button.setAttribute('aria-disabled', 'false');
          button.title = 'Increase quantity by one pack';
        }
      });
    };

    const normalizeQuantity = (announce = false) => {
      if (!quantityInput) return true;
      const quantity = Number.parseInt(quantityInput.value, 10);

      if (Number.isFinite(quantity) && quantity >= 1) {
        quantityInput.value = String(quantity);
        syncQuantitySteps(quantity);
        setQuantityStatus(quantity === 1 ? 'Minimum quantity is one pack.' : quantity + ' packs selected.');
        updateSubmitAccessibility();
        return true;
      }

      quantityInput.value = '1';
      syncQuantitySteps(1);
      setQuantityStatus(announce ? 'Quantity reset to the minimum of one pack.' : 'Minimum quantity is one pack.');
      updateSubmitAccessibility();
      return false;
    };

    const updateSelectedOptionSummary = () => {
      if (!optionSelector) return;
      const option = optionSelector.options[optionSelector.selectedIndex];
      if (!option) return;

      const isAvailable = option.dataset.variantAvailable === 'true';
      if (selectedTitle) selectedTitle.textContent = option.dataset.variantTitle || option.textContent.trim();
      if (selectedPrice) selectedPrice.textContent = option.dataset.variantPrice || '';
      if (selectedStatus) selectedStatus.textContent = option.dataset.variantStatus || (isAvailable ? 'Ready to add to cart' : 'Currently sold out');

      if (submit && form.dataset.cartPending !== 'true') {
        submit.disabled = !isAvailable;
        submit.setAttribute('aria-busy', 'false');
        submit.textContent = isAvailable ? 'Add to cart' : 'Sold out';
      }
      if (submitState) {
        submitState.textContent = isAvailable
          ? 'Add-to-cart stays on this page before checkout opens.'
          : 'Sold-out options stay visible but cannot be added to cart.';
      }
      updateSubmitAccessibility();
    };

    optionSelector && optionSelector.addEventListener('change', updateSelectedOptionSummary);
    quantityInput && quantityInput.addEventListener('change', () => normalizeQuantity(true));
    quantityInput && quantityInput.addEventListener('blur', () => normalizeQuantity(true));
    quantityInput && quantityInput.addEventListener('input', () => {
      if (quantityInput.value === '') {
        setQuantityStatus('Enter at least one pack.');
        updateSubmitAccessibility();
        return;
      }
      normalizeQuantity(false);
    });
    quantitySteps.forEach((button) => {
      button.addEventListener('click', () => {
        if (!quantityInput) return;
        const currentQuantity = Number.parseInt(quantityInput.value, 10);
        const step = Number.parseInt(button.dataset.productQuantityStep, 10);
        const nextQuantity = Math.max(1, (Number.isFinite(currentQuantity) ? currentQuantity : 1) + step);

        quantityInput.value = String(nextQuantity);
        normalizeQuantity(true);
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    updateSelectedOptionSummary();
    normalizeQuantity(false);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (form.dataset.cartPending === 'true') return;

      const status = form.querySelector('[data-product-form-status]');
      const originalLabel = submit ? submit.textContent.trim() : '';
      const wasDisabled = submit ? submit.disabled : false;
      window.clearTimeout(Number(form.dataset.statusTimer));
      resetStatus(status);

      normalizeQuantity(true);

      if (submit) {
        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = 'Adding…';
        updateSubmitAccessibility();
      }
      form.dataset.cartPending = 'true';

      try {
        const fd = new FormData(form);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) throw new Error(await readCartError(res));

        await refreshCartCount();

        if (submit) {
          submit.textContent = 'Added ✓';
          updateSubmitAccessibility();
        }
        setStatus(status, 'Added to cart. You can keep browsing or checkout when ready.', 'success');

        form.dataset.statusTimer = window.setTimeout(() => {
          if (submit) {
            submit.disabled = wasDisabled;
            submit.setAttribute('aria-busy', 'false');
            submit.textContent = originalLabel || 'Add to cart';
            updateSubmitAccessibility();
          }
          resetStatus(status);
        }, 1400);
      } catch (error) {
        const message = error && error.message && error.message !== 'Cart add failed'
          ? error.message
          : 'We could not add this blend. Check the option and quantity, then try again.';
        setStatus(status, message, 'error');

        if (submit) {
          submit.disabled = wasDisabled;
          submit.setAttribute('aria-busy', 'false');
          submit.textContent = 'Try again';
          updateSubmitAccessibility();
        }
        form.dataset.statusTimer = window.setTimeout(() => {
          if (submit) {
            submit.setAttribute('aria-busy', 'false');
            submit.textContent = originalLabel || 'Add to cart';
            updateSubmitAccessibility();
          }
        }, 2200);
      } finally {
        form.dataset.cartPending = 'false';
      }
    });
  });

  document.querySelectorAll('form[data-cart-form]').forEach((form) => {
    const status = form.querySelector('[data-cart-submit-status]');
    const actionState = form.querySelector('[data-cart-action-state]');
    const noteInput = form.querySelector('[data-cart-note-input]');
    const noteStatus = form.querySelector('[data-cart-note-status]');
    const savedState = form.querySelector('[data-cart-saved-state]');
    const checkoutAction = form.querySelector('[data-cart-action][name="checkout"]');
    const checkoutHelper = form.querySelector('[data-cart-checkout-helper]');

    const setCartActionState = (message, mode = '') => {
      if (!actionState) return;
      actionState.textContent = message;
      actionState.dataset.cartActionState = mode;
    };

    const syncCartReviewState = () => {
      const hasDirtyQuantity = Boolean(form.querySelector('[data-cart-quantity-input][data-cart-dirty]'));
      const hasDirtyNote = noteInput && noteInput.value !== noteInput.defaultValue;

      if (noteStatus) {
        noteStatus.textContent = hasDirtyNote
          ? 'Order note changed. Use Update cart before secure checkout.'
          : 'Order note is saved with this cart.';
        noteStatus.dataset.cartNoteStatus = hasDirtyNote ? 'attention' : 'ready';
      }

      if (hasDirtyQuantity || hasDirtyNote) {
        if (savedState) {
          savedState.textContent = 'Unsaved';
          savedState.dataset.cartSavedState = 'attention';
        }
        if (checkoutAction) {
          checkoutAction.disabled = true;
          checkoutAction.setAttribute('aria-disabled', 'true');
        }
        if (checkoutHelper) checkoutHelper.textContent = 'Save edits to unlock checkout';
        setCartActionState('Cart edits are not saved yet. Update cart before secure checkout unlocks.', 'attention');
      } else {
        if (savedState) {
          savedState.textContent = 'Saved';
          savedState.dataset.cartSavedState = 'ready';
        }
        if (checkoutAction) {
          checkoutAction.disabled = false;
          checkoutAction.setAttribute('aria-disabled', 'false');
        }
        if (checkoutHelper) checkoutHelper.textContent = 'Review delivery and payment next';
        setCartActionState('Ready for secure checkout after review. Use Update cart first if quantities or notes change.', 'ready');
      }
    };

    form.querySelectorAll('[data-cart-quantity-input]').forEach((input) => {
      const hint = input.closest('.cart-item__controls')?.querySelector('[data-cart-quantity-hint]');
      const controls = input.closest('.cart-item__controls');
      const quantitySteps = controls ? controls.querySelectorAll('[data-cart-quantity-step]') : [];
      const defaultHint = hint ? hint.textContent : '';
      const productLabel = input.getAttribute('aria-label') || 'this cart line';

      const syncQuantityHint = () => {
        const quantity = Number.parseInt(input.value, 10);
        const isValid = input.value !== '' && !Number.isNaN(quantity) && quantity >= 0;
        const hasChanged = isValid && String(quantity) !== input.defaultValue;

        input.toggleAttribute('data-cart-dirty', hasChanged);
        if (controls) controls.toggleAttribute('data-cart-line-dirty', hasChanged);
        quantitySteps.forEach((button) => {
          const isDecrease = button.dataset.cartQuantityStep === '-1';
          if (isDecrease) button.disabled = !isValid || quantity <= 0;
          const nextQuantity = isDecrease
            ? Math.max(0, (Number.isFinite(quantity) ? quantity : 0) - 1)
            : (Number.isFinite(quantity) ? quantity : 0) + 1;
          button.title = isDecrease
            ? (button.disabled ? 'Quantity is already zero for ' + productLabel : 'Decrease ' + productLabel + ' to ' + nextQuantity)
            : 'Increase ' + productLabel + ' to ' + nextQuantity;
          button.setAttribute('aria-label', button.title);
        });
        if (!hint) return;
        hint.textContent = hasChanged
          ? 'Quantity changed. Use Update cart to save before checkout.'
          : defaultHint;
        syncCartReviewState();
      };

      input.addEventListener('input', () => {
        const quantity = Number.parseInt(input.value, 10);
        if (input.value === '' || Number.isNaN(quantity) || quantity < 0) return;
        input.value = String(quantity);
        syncQuantityHint();
      });
      input.addEventListener('blur', () => {
        const quantity = Number.parseInt(input.value, 10);
        if (input.value === '' || Number.isNaN(quantity) || quantity < 0) input.value = '0';
        syncQuantityHint();
      });
      quantitySteps.forEach((button) => {
        button.addEventListener('click', () => {
          const currentQuantity = Number.parseInt(input.value, 10);
          const step = Number.parseInt(button.dataset.cartQuantityStep, 10);
          const nextQuantity = Math.max(0, (Number.isFinite(currentQuantity) ? currentQuantity : 0) + step);
          input.value = String(nextQuantity);
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
        });
      });
      syncQuantityHint();
    });

    if (noteInput) {
      noteInput.addEventListener('input', syncCartReviewState);
      syncCartReviewState();
    }

    form.addEventListener('submit', (event) => {
      const submitter = event.submitter && event.submitter.matches('[data-cart-action]')
        ? event.submitter
        : form.querySelector('[data-cart-action]');

      form.querySelectorAll('[data-cart-action]').forEach((button) => {
        button.setAttribute('aria-busy', button === submitter ? 'true' : 'false');
      });

      if (!status) return;
      const message = submitter && submitter.name === 'checkout'
        ? 'Opening secure Shopify checkout. Delivery and payment details are reviewed next.'
        : 'Saving cart quantities and order note. The updated cart summary will load next.';
      status.textContent = message;
      setCartActionState(message, submitter && submitter.name === 'checkout' ? 'checkout' : 'saving');
    });
  });

  document.querySelectorAll('form[data-customer-auth-form]').forEach((form) => {
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-customer-auth-status]');

    form.addEventListener('submit', () => {
      if (submit) {
        submit.setAttribute('aria-busy', 'true');
        submit.dataset.originalLabel = submit.dataset.originalLabel || submit.textContent.trim();
        submit.textContent = form.dataset.submitLabel || 'Sending...';
      }

      if (status) {
        status.textContent = form.dataset.submitLabel || 'Sending...';
      }
    });
  });

  document.querySelectorAll('form[data-search-form]').forEach((form) => {
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-search-submit-status]');
    const input = form.querySelector('[data-search-input]');
    const clear = form.querySelector('[data-search-clear]');
    const scopeStatus = form.querySelector('[data-search-scope-status]');

    const scopeMessages = {
      'product,article,page': 'All lane selected: results will include blends, articles and pages.',
      product: 'Product lane selected: results will focus on blends, formats, prices and product details.',
      'article,page': 'Guide lane selected: results will focus on articles and support pages.'
    };

    const scopeLoadingMessages = {
      'product,article,page': 'Product, article and page results will load next.',
      product: 'Blend results will load next: formats, prices and product details.',
      'article,page': 'Guide results will load next: articles and support pages.'
    };

    const getCheckedScopeValue = () => {
      const checkedScope = form.querySelector('input[name="type"]:checked');
      return checkedScope && checkedScope.value ? checkedScope.value : 'product,article,page';
    };

    const updateScopeStatus = () => {
      const scopeValue = getCheckedScopeValue();
      if (scopeStatus) {
        scopeStatus.textContent = scopeMessages[scopeValue] || scopeMessages['product,article,page'];
      }
      if (submit && form.dataset.cartPending !== 'true') {
        submit.setAttribute('aria-label', 'Search My Life Tea. ' + (scopeLoadingMessages[scopeValue] || scopeLoadingMessages['product,article,page']));
      }
    };

    const clearSearchError = () => {
      if (input) input.removeAttribute('aria-invalid');
      if (status) {
        status.textContent = '';
        status.classList.remove('is-error');
      }
    };

    input && input.addEventListener('input', clearSearchError);
    clear && clear.addEventListener('click', () => {
      if (!input) return;
      input.value = '';
      clearSearchError();
      input.focus();
      if (status) {
        status.textContent = 'Search cleared. Enter a tea, ingredient or ritual to start again.';
        status.classList.remove('is-error');
      }
    });
    form.querySelectorAll('input[name="type"]').forEach((scope) => {
      scope.addEventListener('change', updateScopeStatus);
    });
    updateScopeStatus();

    form.addEventListener('submit', (event) => {
      const label = form.dataset.submitLabel || 'Searching...';

      if (input && input.value.trim() === '') {
        event.preventDefault();
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        if (status) {
          status.textContent = 'Enter a tea, ingredient or ritual before searching.';
          status.classList.add('is-error');
        }
        return;
      }

      if (submit) {
        submit.setAttribute('aria-busy', 'true');
        submit.dataset.originalLabel = submit.dataset.originalLabel || submit.textContent.trim();
        submit.textContent = label;
      }

      if (status) {
        const scopeValue = getCheckedScopeValue();
        const loadingMessage = scopeLoadingMessages[scopeValue] || scopeLoadingMessages['product,article,page'];
        status.textContent = label + ' ' + loadingMessage;
        status.classList.remove('is-error');
      }
    });
  });

  document.querySelectorAll('form[data-collection-sort-form]').forEach((form) => {
    const select = form.querySelector('[data-collection-sort-select]');
    const submit = form.querySelector('[data-collection-sort-submit]');
    const status = form.querySelector('[data-collection-sort-status]');

    const selectedSortLabel = () => (
      select && select.selectedOptions.length
        ? select.selectedOptions[0].textContent.trim()
        : 'selected order'
    );

    const previewSortStatus = () => {
      if (status) {
        status.textContent = selectedSortLabel() + ' selected. Use Apply sort to reload the collection in that order.';
        status.classList.remove('is-busy');
      }
    };

    const setSortStatus = () => {
      const selectedLabel = select && select.selectedOptions.length
        ? select.selectedOptions[0].textContent.trim()
        : 'selected order';
      const label = form.dataset.submitLabel || 'Applying sort...';

      if (submit) {
        submit.setAttribute('aria-busy', 'true');
        submit.textContent = label;
      }

      if (status) {
        status.textContent = 'Applying ' + selectedLabel + '. The sorted collection will load next.';
        status.classList.add('is-busy');
      }
    };

    select && select.addEventListener('change', previewSortStatus);
    form.addEventListener('submit', setSortStatus);
  });

  document.querySelectorAll('[data-collection-filter-assist-status]').forEach((status) => {
    const assist = status.closest('.collection-filter-assist');
    if (!assist) return;

    assist.querySelectorAll('[data-collection-filter-action]').forEach((link) => {
      link.addEventListener('click', () => {
        status.textContent = link.dataset.collectionFilterAction || 'Loading the selected collection route.';
        status.classList.add('is-busy');
      });
    });
  });

  document.querySelectorAll('form[data-newsletter-form]').forEach((form) => {
    const submit = form.querySelector('[type="submit"]');
    const status = form.querySelector('[data-newsletter-status]');
    const email = form.querySelector('input[type="email"]');
    const consent = form.querySelector('[data-newsletter-consent]');
    const consentHelp = form.querySelector('[data-newsletter-consent-help]');

    const defaultConsentHelp = consentHelp ? consentHelp.textContent.trim() : '';
    const defaultStatus = status ? status.textContent.trim() : '';
    const setConsentFeedback = () => {
      if (!consent || !consentHelp) return;

      const hasConsent = consent.checked;
      consentHelp.textContent = hasConsent
        ? 'Consent selected. You can subscribe now or keep browsing without joining.'
        : defaultConsentHelp;
      consentHelp.classList.toggle('is-error', !hasConsent && consent.matches(':user-invalid'));
    };
    const setEmailFeedback = () => {
      if (!email) return true;

      const hasValidEmail = email.checkValidity();
      email.setAttribute('aria-invalid', hasValidEmail ? 'false' : 'true');
      if (hasValidEmail && status && status.textContent.trim() === defaultStatus) {
        status.classList.remove('is-error');
      }
      return hasValidEmail;
    };

    if (consent) {
      consent.addEventListener('change', setConsentFeedback);
      consent.addEventListener('invalid', () => {
        if (consentHelp) {
          consentHelp.textContent = 'Tick the consent box before subscribing. Shopping, search and support remain available without joining.';
          consentHelp.classList.add('is-error');
        }
        if (status) {
          status.textContent = 'Newsletter signup needs consent before Shopify can save it.';
          status.classList.add('is-error');
        }
      });
    }
    if (email) {
      email.addEventListener('input', setEmailFeedback);
      email.addEventListener('invalid', () => {
        email.setAttribute('aria-invalid', 'true');
        if (status) {
          status.textContent = 'Enter a valid email address before subscribing.';
          status.classList.add('is-error');
        }
      });
    }

    form.addEventListener('submit', () => {
      const label = form.dataset.submitLabel || 'Subscribing...';
      setConsentFeedback();
      setEmailFeedback();

      if (submit) {
        submit.setAttribute('aria-busy', 'true');
        submit.dataset.originalLabel = submit.dataset.originalLabel || submit.textContent.trim();
        submit.textContent = label;
      }

      if (status) {
        status.textContent = label + ' Shopify will confirm the signup after the form finishes.';
        status.classList.remove('is-error');
      }
    });
  });

  document.querySelectorAll('[data-chat-assistant]').forEach((assistant) => {
    const toggle = assistant.querySelector('[data-chat-assistant-toggle]');
    let initializeChatAssistant;
    let chatAssistantReady = false;

    const ensureChatAssistant = () => {
      if (chatAssistantReady || !initializeChatAssistant) return;
      chatAssistantReady = true;
      initializeChatAssistant();
    };

    toggle && toggle.addEventListener('pointerenter', ensureChatAssistant, { once: true });
    toggle && toggle.addEventListener('focus', ensureChatAssistant, { once: true });
    toggle && toggle.addEventListener('click', () => {
      assistant.dataset.chatAssistantOpenRequested = 'true';
      ensureChatAssistant();
    }, { once: true });

    initializeChatAssistant = () => {
    const close = assistant.querySelector('[data-chat-assistant-close]');
    const panel = assistant.querySelector('#ChatAssistantPanel');
    const messages = assistant.querySelector('[data-chat-assistant-messages]');
    const form = assistant.querySelector('[data-chat-assistant-form]');
    const input = assistant.querySelector('[data-chat-assistant-input]');
    const submit = form ? form.querySelector('[type="submit"]') : null;
    const submitStatus = assistant.querySelector('[data-chat-assistant-status]');
    const contactUrl = assistant.dataset.contactUrl || '/pages/contact';
    const searchUrl = assistant.dataset.searchUrl || '/search';
    const productsUrl = assistant.dataset.productsUrl || '/collections/all';
    const cartUrl = assistant.dataset.cartUrl || '/cart';
    const shippingUrl = assistant.dataset.shippingUrl || '/policies/shipping-policy';
    const returnsUrl = assistant.dataset.returnsUrl || '/policies/refund-policy';

    const makeSearchUrl = (query, type = 'product%2Carticle%2Cpage') => searchUrl + '?type=' + type + '&q=' + encodeURIComponent(query);
    const answerBank = [
      {
        keys: ['caffeine', 'decaf', 'caffeine-free', 'sleep', 'evening', 'sensitive'],
        text: 'For caffeine fit, start with the caffeine notes on each product page and use caffeine-free search if you are avoiding it. If you are pregnant, breastfeeding, caffeine-sensitive or taking medication, check the label guidance before regular use.',
        actions: [
          { label: 'Caffeine-free blends', href: makeSearchUrl('caffeine-free', 'product') },
          { label: 'Ask about caffeine', href: contactUrl }
        ]
      },
      {
        keys: ['ingredient', 'ingredients', 'allergy', 'allergen', 'medication', 'pregnant', 'breastfeeding'],
        text: 'Ingredient questions are worth checking carefully. Product pages list blend details where available, and support can help if you need a specific ingredient or label question answered before buying.',
        actions: [
          { label: 'Search ingredients', href: makeSearchUrl('ingredients') },
          { label: 'Contact support', href: contactUrl }
        ]
      },
      {
        keys: ['ship', 'shipping', 'delivery', 'postage', 'when arrive'],
        text: 'Shipping options, final costs and delivery details are confirmed through Shopify before payment. You can review the policy first or contact support if the order timing matters.',
        actions: [
          { label: 'Shipping policy', href: shippingUrl },
          { label: 'Ask support', href: contactUrl }
        ]
      },
      {
        keys: ['return', 'refund', 'exchange', 'cancel'],
        text: 'Returns and refunds follow the store policy. Check the policy before ordering if you are unsure, especially for gifts or multiple-blend orders.',
        actions: [
          { label: 'Returns policy', href: returnsUrl },
          { label: 'Contact support', href: contactUrl }
        ]
      },
      {
        keys: ['gift', 'present', 'birthday', 'bundle'],
        text: "For gifts, choose by flavour preference, caffeine fit and the recipient's daily routine. If you are unsure, compare the full range before checkout.",
        actions: [
          { label: 'Gift ideas', href: makeSearchUrl('gift', 'product') },
          { label: 'Compare blends', href: productsUrl }
        ]
      },
      {
        keys: ['brew', 'steep', 'prepare', 'how make', 'taste', 'flavour', 'flavor'],
        text: 'For brewing, start with the product page guidance and adjust steep time to taste. Search the journal if you want more ritual or ingredient context.',
        actions: [
          { label: 'Brew guides', href: makeSearchUrl('brew guide', 'article%2Cpage') },
          { label: 'Shop blends', href: productsUrl }
        ]
      },
      {
        keys: ['order', 'cart', 'checkout', 'payment', 'quantity'],
        text: 'You can review quantities in the cart before checkout. Payment, taxes, shipping and final delivery options are confirmed by Shopify before payment.',
        actions: [
          { label: 'View cart', href: cartUrl },
          { label: 'Contact support', href: contactUrl }
        ]
      },
      {
        keys: ['choose', 'recommend', 'best', 'which', 'blend', 'focus', 'calm', 'digestion', 'energy'],
        text: 'The quickest route is to choose by daily ritual: focus, evening, digestion, flavour, caffeine fit, or Greek-god story. Browse the range or search the goal you have in mind.',
        actions: [
          { label: 'Browse all blends', href: productsUrl },
          { label: 'Search by goal', href: makeSearchUrl('focus digestion evening', 'product') }
        ]
      }
    ];

    const fallbackAnswer = (question) => ({
      text: 'I can help route that. Try searching the store, browse all blends, or send the question to support if it needs a specific order, ingredient or policy answer.',
      actions: [
        { label: 'Search this question', href: makeSearchUrl(question || 'tea') },
        { label: 'Contact support', href: contactUrl }
      ]
    });

    const addMessage = (text, speaker, actions = []) => {
      if (!messages) return;
      const item = document.createElement('div');
      item.className = 'chat-assistant__message chat-assistant__message--' + speaker;
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      item.append(paragraph);

      if (actions.length) {
        const actionList = document.createElement('div');
        actionList.className = 'chat-assistant__message-actions';
        actions.forEach((action) => {
          const link = document.createElement('a');
          link.href = action.href;
          link.textContent = action.label;
          actionList.append(link);
        });
        item.append(actionList);
      }

      messages.append(item);
      messages.scrollTop = messages.scrollHeight;
    };

    const setSubmitState = (message, busy = false) => {
      if (submitStatus) submitStatus.textContent = message;
      if (form) form.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (submit) {
        submit.setAttribute('aria-busy', busy ? 'true' : 'false');
        submit.textContent = busy ? 'Routing...' : 'Send';
      }
    };

    const answerQuestion = (question) => {
      const normalized = question.toLowerCase();
      const answer = answerBank.find((entry) => entry.keys.some((key) => normalized.includes(key))) || fallbackAnswer(question);
      addMessage(question, 'user');
      setSubmitState('Routing your question to the best MyLifeTea help path.', true);
      window.setTimeout(() => {
        addMessage(answer.text, 'bot', answer.actions);
        setSubmitState('Answer added. Use the suggested routes or ask another question.', false);
      }, 140);
    };

    const openPanel = () => {
      if (!panel || !toggle) return;
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      window.setTimeout(() => input && input.focus(), 0);
    };

    const closePanel = () => {
      if (!panel || !toggle) return;
      panel.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    };

    toggle && toggle.addEventListener('click', () => {
      if (panel && panel.hidden) openPanel();
      else closePanel();
    });
    close && close.addEventListener('click', closePanel);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel && !panel.hidden) closePanel();
    });

    assistant.querySelectorAll('[data-chat-question]').forEach((button) => {
      button.addEventListener('click', () => {
        openPanel();
        assistant.querySelectorAll('[data-chat-question]').forEach((quickButton) => {
          const isCurrentButton = quickButton === button;
          quickButton.classList.toggle('is-active', isCurrentButton);
          quickButton.setAttribute('aria-pressed', isCurrentButton ? 'true' : 'false');
        });
        answerQuestion(button.dataset.chatQuestion || button.textContent.trim());
        window.setTimeout(() => {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        }, 900);
      });
    });

    form && form.addEventListener('submit', (event) => {
      event.preventDefault();
      const question = input ? input.value.trim() : '';
      if (!question) {
        setSubmitState('Type a question first, then send it.', false);
        input && input.focus();
        return;
      }
      answerQuestion(question);
      input.value = '';
    });

    if (assistant.dataset.chatAssistantOpenRequested === 'true') {
      delete assistant.dataset.chatAssistantOpenRequested;
      openPanel();
    }
    };
  });
});
